import { useState } from "react";
import Page from "../../../../components/Layout/Page";
import { useIsMobile } from "../../../../helpers/ui";
import { colors } from "../../../../styles";
import { DesktopHeader, MobileHeader } from "../Account/components";
import {
  employeeAccountPath,
  employeeDebitCardInfoPath,
} from "../../../../routes/paths";
import HeaderBreadcrumbs from "../../../../components/HeaderBreadcrumbs";
import T from "../../../../components/Typography";
import ResponsePageContent from "../../../../components/Layout/ResponsivePageContent";
import { useNavigate } from "react-router-dom";
import {
  FieldHeader,
  SectionFlexColumn,
  TextInput,
} from "../../../../components/General";
import Button from "../../../../components/Button";
import useGetAccount from "../../../../hooks/queries/employee/useGetAccount";
import Loader from "../../../../components/Loader";
import { showDebitCardFeature } from "../../../../helpers/debitcard";
import ErrorPage from "../../../../components/ErrorPage";
import { toast } from "react-toastify";
import useSetCardPin from "../../../../hooks/mutations/employee/useSetCardPin";

const DebitCardChangePin = () => {
  const { data, status } = useGetAccount();
  const { mutateAsync: updatePin, status: updatePinStatus } = useSetCardPin();
  const [last4, setLast4] = useState("");
  const [pin, setPin] = useState("");
  const [confirmedPin, setConfirmedPin] = useState("");
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const title = "Change PIN";
  const subtitle = "Keep your card secure";
  const breadcrumbs = ["My Account", "Debit Card", title];

  if (status === "loading" || updatePinStatus === "loading") return <Loader />;
  if (status === "error") return <ErrorPage />;

  const doBack = () => {
    navigate(employeeDebitCardInfoPath);
  };

  const handleSave = async () => {
    if (last4.length !== 4) {
      toast.error("Last 4 digits must be entered");
    }
    if (pin !== confirmedPin) {
      toast.error("Pins do not match");
      return;
    }

    try {
      const cardId = data.challenger_saving.debit_card.latest_card_id;
      if (cardId && pin.length === 4) {
        const response = await updatePin({ cardId, last4, pin });
        if (!response.err) {
          toast.success("Pin updated successfully!");
          doBack();
          return;
        }
      }
    } catch (error) {
      toast.error("Unable to update pin");
    }
  };

  const renderFeatureDisabled = () => {
    return (
      <div>
        <T variant="lgReg" color={colors.red}>
          Debit cards are not enabled for your program.
        </T>
      </div>
    );
  };

  const featureEnabled = showDebitCardFeature(
    data.challenger_saving.debit_card
  );

  return (
    <Page>
      {isMobile ? (
        <MobileHeader
          title={title}
          showBack
          showClose
          handleBack={doBack}
          closeRoutePath={employeeAccountPath}
        />
      ) : (
        <DesktopHeader>
          <HeaderBreadcrumbs breadcrumbs={breadcrumbs} handleBack={doBack} />
        </DesktopHeader>
      )}
      <ResponsePageContent title={title} subtitle={subtitle}>
        {featureEnabled ? (
          <SectionFlexColumn>
            <FieldHeader>Last 4 Digits on Card</FieldHeader>
            <TextInput
              size="small"
              placeholder="Last 4 digits on card"
              type="number"
              value={last4}
              onChange={(e) => {
                if (e.target.value.length <= 4) {
                  setLast4(e.target.value);
                }
              }}
            />
            <FieldHeader>New PIN</FieldHeader>
            <TextInput
              size="small"
              placeholder="New pin"
              type="number"
              value={pin}
              onChange={(e) => {
                if (e.target.value.length <= 4) {
                  setPin(e.target.value);
                }
              }}
            />
            <T variant="smReg" color={colors.gray}>
              PIN must be four numbers
            </T>
            <FieldHeader sx={{ marginTop: "8px" }}>Confirm PIN</FieldHeader>
            <TextInput
              size="small"
              placeholder="Confirm pin"
              type="number"
              value={confirmedPin}
              onChange={(e) => {
                if (e.target.value.length <= 4) {
                  setConfirmedPin(e.target.value);
                }
              }}
            />
            <Button
              onClick={handleSave}
              disabled={
                last4.length < 4 || pin.length < 4 || confirmedPin.length < 4
              }
            >
              Save
            </Button>
          </SectionFlexColumn>
        ) : (
          renderFeatureDisabled()
        )}
      </ResponsePageContent>
    </Page>
  );
};

export default DebitCardChangePin;
